import { useLoaderData } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { getFeaturedWork } from '../utils/featured-work';
import { upscaleImageHandlerUrl } from '../utils/upscale-image';

/**
 * Feature project showcase (Figma §3).
 *
 * The lead featured project's image sits on white and overlaps the dark band
 * below — the black background starts ~84% down the image (Figma: image
 * y2198–3318, black bg top y3140), so the image "sticks out" of the dark band.
 * The band holds the project caption and then carries straight into the
 * Selected-work section (also dark), which owns the run of black between them.
 */
interface ProfessionalAboutSectionProps {
    /**
     * Whether Selected work renders below. It owns the run of black under the
     * caption, so when it stands down this band has to close itself instead —
     * the two sections choose their items differently, and a professional whose
     * only work is associated projects reaches this one but not that one.
     */
    hasSelectedWork?: boolean;
}

export const ProfessionalAboutSection = ({
    hasSelectedWork = true,
}: ProfessionalAboutSectionProps = {}) => {
    const { featuredProjects, associatedProjects } =
        useLoaderData<typeof loader>();

    // Product suppliers (e.g. Resene) have no projects of their own — fall back
    // to associated projects so the feature image isn't empty.
    const lead = getFeaturedWork(
        featuredProjects.featuredProjectsList,
        associatedProjects,
    )[0];

    // Without a lead there is no image and no caption, so the band would render
    // as an empty dark strip.
    if (!lead) return null;

    return (
        // Desktop-only: the lead feature image is a magazine device. On mobile
        // the Selected-work grid below leads instead, so the whole band is
        // hidden rather than squeezed.
        <section className="relative w-full max-md:hidden">
            {/* Feature image — on white, overlapping the dark band (kept on top). */}
            <div className="relative z-10 mx-auto w-full max-w-[120rem] md:px-[3rem]">
                <RoccoLink
                    to={lead.link}
                    className="bg-neutral-900 relative block w-full overflow-hidden rounded-tile max-md:aspect-[414/300] md:aspect-[1820/1102]"
                >
                    <img
                        src={upscaleImageHandlerUrl(lead.images?.[0]?.src)}
                        alt={lead.images?.[0]?.alt ?? lead.title}
                        className="absolute inset-0 size-full object-cover"
                        loading="lazy"
                    />
                </RoccoLink>
            </div>

            {/* Dark band — slid up behind the image's lower ~16% so the image
                sticks out, with pt dropping the caption just below it.
                Selected-work's own top padding is normally the whole 100px run
                of black between the caption and its heading, so this carries a
                bottom padding only when that section is not there to do it. */}
            <div
                className={cn(
                    'bg-[#181818] text-white md:-mt-[13.1rem] md:pt-[16.225rem]',
                    !hasSelectedWork && 'md:pb-[6.25rem]',
                )}
            >
                <div className="mx-auto w-full max-w-[120rem] max-md:px-6 max-md:pt-4 md:pl-[2.3124rem]">
                    <p className="m-0 font-sans leading-tight font-normal text-white max-md:text-base md:text-[1.248rem]">
                        {lead.title}
                    </p>
                    {lead.professional?.name ? (
                        <p className="m-0 font-sans leading-tight text-white/50 max-md:text-sm md:text-[1.08rem]">
                            {lead.professional.name}
                        </p>
                    ) : null}
                </div>
            </div>
        </section>
    );
};
